/**
 * DictateSalesPage — hustledictate.au front door.
 *
 * Sales page for the HustleDictate Chrome extension: speech-to-text
 * on any form, on any website. Free for HustleSuite Founder Members,
 * $5/month otherwise. Emphasises the RE tenancy / NDIS / invoice
 * use-cases that Dail explicitly called out.
 *
 * iter253: Converged onto the shared PlatformHeader + SuiteFooter
 * chrome so hustledictate.au visually lines up with every other
 * module (Meetups, Travel, Roundup, …). Sweeps in dark: variants
 * everywhere so the page reads cleanly on both light and dark
 * backgrounds, and hooks every user-facing string through i18n so
 * language switching works on this page too.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mic, Zap, ShieldCheck, ArrowRight, Chrome } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const CHROME_STORE = "https://chrome.google.com/webstore/detail/hustlesuite-assistant/";
const HP = "https://app.hustleplaza.au";

export default function DictateSalesPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = t("dictate_meta_title", {
      defaultValue: "HustleDictate — Speak. Any form. Any website.",
    });
  }, [i18n.language, t]);

  const useCases = [
    ["dictate_uc_re_t",      "dictate_uc_re_b"],
    ["dictate_uc_ndis_t",    "dictate_uc_ndis_b"],
    ["dictate_uc_invoice_t", "dictate_uc_invoice_b"],
    ["dictate_uc_email_t",   "dictate_uc_email_b"],
  ];

  const NAV = [
    { to: "/",         label: t("dictate_nav_home",    { defaultValue: "Home" }) },
    { to: "#pricing",  label: t("dictate_nav_pricing", { defaultValue: "Pricing" }) },
    {
      to: CHROME_STORE,
      label: t("dictate_cta_install", { defaultValue: "Add to Chrome" }),
      align: "right",
      variant: "primary",
      external: true,
    },
    {
      to: `${HP}/register?founder=1&mod=dictate`,
      label: t("dictate_cta_founder_short", { defaultValue: "Founder Signup" }),
      align: "right",
      external: true,
    },
  ];

  return (
    <div
      data-testid="dictate-sales-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: "HustleDictate", to: "/" }}
        nav={NAV}
        showBookCta={false}
        showCart={false}
      />

      <header className="max-w-6xl mx-auto px-6 py-16 md:py-24 text-center">
        <div className="mx-auto w-20 h-20 rounded-full bg-fuchsia-500/15 dark:bg-fuchsia-400/10 flex items-center justify-center">
          <Mic size={36} className="text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <p className="mt-6 text-fuchsia-600 dark:text-fuchsia-400 text-[11px] font-bold uppercase tracking-[0.32em]">
          {t("dictate_hero_kicker", { defaultValue: "HustleDictate" })}
        </p>
        <h1 className="mt-3 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl mx-auto">
          {t("dictate_hero_title", { defaultValue: "Speak. Any form. Any website." })}
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-stone-700 dark:text-stone-300 leading-relaxed">
          {t("dictate_hero_sub", {
            defaultValue:
              "Press Alt+M on any web page. Speak. HustleDictate transcribes what you said and drops it straight into whichever input you clicked. Real-estate applications, NDIS paperwork, invoices, emails — cut form-filling time in half.",
          })}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={CHROME_STORE}
            target="_blank"
            rel="noreferrer"
            data-testid="dictate-cta-primary"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-black text-slate-950 bg-fuchsia-500 hover:bg-fuchsia-400 dark:bg-fuchsia-400 dark:hover:bg-fuchsia-300 shadow-[0_0_60px_-15px_#e879f9aa]"
          >
            <Chrome size={16} /> {t("dictate_cta_install", { defaultValue: "Add to Chrome" })}
          </a>
          <a
            href={`${HP}/register?founder=1&mod=dictate`}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-semibold text-stone-900 dark:text-stone-100 border border-stone-300 dark:border-stone-700 hover:border-fuchsia-500 hover:text-fuchsia-600 dark:hover:text-fuchsia-400 transition-colors"
          >
            {t("dictate_cta_founder", { defaultValue: "Free for Founder Members" })}
          </a>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-stone-600 dark:text-stone-500">
          <ShieldCheck size={12} className="text-emerald-600 dark:text-emerald-400" />
          {t("dictate_hero_privacy", {
            defaultValue:
              "Audio never leaves your device except the moment we transcribe. Nothing is stored.",
          })}
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-3xl font-black text-center mb-10">
          {t("dictate_uc_title", { defaultValue: "Where it saves you time" })}
        </h2>
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="dictate-use-cases"
        >
          {useCases.map(([tk, bk]) => (
            <div
              key={tk}
              className="rounded-2xl border border-stone-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 p-6 shadow-sm dark:shadow-none"
            >
              <Zap size={20} className="text-fuchsia-600 dark:text-fuchsia-400" />
              <h3 className="mt-4 font-bold text-stone-900 dark:text-stone-100">{t(tk)}</h3>
              <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">{t(bk)}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        id="pricing"
        className="max-w-3xl mx-auto px-6 pb-24 text-center scroll-mt-24"
      >
        <div className="rounded-2xl border border-fuchsia-500/40 dark:border-fuchsia-400/30 bg-fuchsia-500/5 dark:bg-fuchsia-400/5 p-8">
          <p className="text-fuchsia-600 dark:text-fuchsia-400 text-[11px] font-bold uppercase tracking-[0.28em]">
            {t("dictate_pricing_kicker", { defaultValue: "Pricing" })}
          </p>
          <h2 className="mt-3 text-2xl md:text-3xl font-black">
            {t("dictate_pricing_title", {
              defaultValue: "Free for Founder Members. $5/mo otherwise.",
            })}
          </h2>
          <p className="mt-4 text-stone-700 dark:text-stone-300">
            {t("dictate_pricing_sub", {
              defaultValue:
                "Join HustleSuite as a Founder Member and HustleDictate is yours at no cost. Otherwise it's a flat $5 AUD/month with unlimited transcription minutes.",
            })}
          </p>
          <a
            href={`${HP}/register?founder=1&mod=dictate`}
            className="mt-6 inline-flex items-center gap-2 h-12 px-8 rounded-full text-base font-black text-slate-950 bg-fuchsia-500 hover:bg-fuchsia-400 dark:bg-fuchsia-400 dark:hover:bg-fuchsia-300"
          >
            {t("dictate_cta_join", { defaultValue: "Become a Founder Member" })}
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <SuiteFooter />
    </div>
  );
}
